"use client"

import { useState, useRef } from "react"
import { X, Plus, Image, Loader2, Tag, AlertCircle } from "lucide-react"
import LocationAutocomplete from "./LocationAutocomplete"

const CATEGORIES = [
  "Plumbing", "Electrical", "Carpentry", "Painting", "Roofing", "HVAC",
  "Landscaping", "Concrete", "Construction", "Flooring", "Tiling", "Drywall", "Masonry",
  "General Contracting", "Renovation", "New Construction", "Other",
]

interface Props {
  onClose: () => void
  onCreated: (project: unknown) => void
  userLocation?: string | null
}

export default function CreateProjectModal({ onClose, onCreated, userLocation }: Props) {
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [location, setLocation] = useState(userLocation || "")
  const [category, setCategory] = useState("")
  const [budget, setBudget] = useState("")
  const [timeline, setTimeline] = useState("")
  const [urgency, setUrgency] = useState<"flexible" | "normal" | "urgent">("normal")
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [images, setImages] = useState<string[]>([])
  const [imageUploading, setImageUploading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const imageRef = useRef<HTMLInputElement>(null)

  const addTag = () => {
    const t = tagInput.trim()
    if (t && !tags.includes(t) && tags.length < 8) {
      setTags([...tags, t])
      setTagInput("")
    }
  }

  const removeTag = (t: string) => setTags(tags.filter((x) => x !== t))

  const handleImageAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    if (images.length + files.length > 5) { setError("Maximum 5 images per project"); return }

    setImageUploading(true)
    setError("")
    const uploaded: string[] = []
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { setError("Each image must be under 10 MB"); continue }
      const fd = new FormData()
      fd.append("file", file)
      const res = await fetch("/api/posts/upload-image", { method: "POST", body: fd })
      const data = await res.json()
      if (res.ok && data.url) uploaded.push(data.url)
    }
    setImages((prev) => [...prev, ...uploaded])
    setImageUploading(false)
    if (imageRef.current) imageRef.current.value = ""
  }

  const removeImage = (url: string) => setImages(images.filter((i) => i !== url))

  const handleSubmit = async () => {
    if (!title.trim()) { setError("Title is required"); return }
    if (!description.trim()) { setError("Description is required"); return }
    if (!location.trim()) { setError("Location is required"); return }
    if (!category) { setError("Category is required"); return }

    setIsSubmitting(true)
    setError("")

    const res = await fetch("/api/projects", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, description, location, category, budget, timeline, urgency, tags, images }),
    })
    const data = await res.json()
    setIsSubmitting(false)

    if (!res.ok) { setError(data.error || "Failed to create project"); return }
    onCreated(data)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Post a Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Title *</label>
            <input
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Kitchen renovation, Roof repair, Electrical panel upgrade"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={4} maxLength={3000}
              placeholder="Describe the work needed, scope, any specific requirements…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Category + Urgency row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select…</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
              <select
                value={urgency} onChange={(e) => setUrgency(e.target.value as "flexible" | "normal" | "urgent")}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="flexible">Flexible</option>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
            <LocationAutocomplete value={location} onChange={setLocation} />
          </div>

          {/* Budget + Timeline row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                value={budget} onChange={(e) => setBudget(e.target.value)}
                placeholder="e.g. $5,000–$8,000"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timeline <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                value={timeline} onChange={(e) => setTimeline(e.target.value)}
                placeholder="e.g. 2 weeks, ASAP"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags <span className="text-gray-400 font-normal">(optional, up to 8)</span></label>
            <div className="flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag() } }}
                placeholder="Add a tag…"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button type="button" onClick={addTag} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map((t) => (
                  <span key={t} className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">
                    <Tag className="w-3 h-3" />
                    {t}
                    <button type="button" onClick={() => removeTag(t)} className="ml-0.5 hover:text-blue-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Photos <span className="text-gray-400 font-normal">(optional, up to 5)</span></label>
            <div className="flex flex-wrap gap-2">
              {images.map((url) => (
                <div key={url} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(url)}
                    className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {images.length < 5 && (
                <button
                  type="button"
                  onClick={() => imageRef.current?.click()}
                  disabled={imageUploading}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  {imageUploading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : <Image className="w-5 h-5 text-gray-400" />}
                  <span className="text-xs text-gray-400">Add</span>
                </button>
              )}
            </div>
            <input ref={imageRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageAdd} />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex-shrink-0 flex gap-3 justify-end"
          style={{ paddingBottom: "max(1.25rem, calc(env(safe-area-inset-bottom) + 72px))" }}>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || imageUploading}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? "Posting…" : "Post Project"}
          </button>
        </div>
      </div>
    </div>
  )
}
